import { useEffect, useMemo } from 'react';
import {
  useSelectedEntityId,
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import request from 'utils/request';
import { setAppLoading, showSnackbar } from 'stores/useUiStore';
import { useOrganizationsList } from 'stores/useOrganizationsStore';
import { TOKEN_INVALID } from 'containers/Base/useBaseViewData';
import type { ApiRespBody } from 'types/api';
import type { OrganizationsState } from 'containers/Organizations/organizations.types';

import type { Device, DeviceType, DevicesState } from '../devices.types';


export const DEVICES_QUERY_KEY = ['devices'] as const;
export const DEVICE_TYPES_QUERY_KEY = ['deviceTypes'] as const;

export async function fetchDevices(
  authToken: string,
  signal?: AbortSignal
): Promise<Device[]> {
  const result = await request<ApiRespBody<Device[]>>('/api/devices', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success) return result.data ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load devices');
}

export async function fetchDeviceTypes(
  authToken: string,
  signal?: AbortSignal
): Promise<DeviceType[]> {
  const result = await request<ApiRespBody<DeviceType[]>>('/api/devices/types', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', authorization: authToken },
    ...(signal ? { signal } : {}),
  });

  if (result?.success) return result.data ?? [];
  if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
  throw new Error(result?.message || 'Failed to load device types');
}

export function devicesQueryOptions(
  authToken: string,
  organizationId: number | string | null
) {
  return queryOptions({
    queryKey: [...DEVICES_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchDevices(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

export function deviceTypesQueryOptions(
  authToken: string,
  organizationId: number | string | null
) {
  return queryOptions({
    queryKey: [...DEVICE_TYPES_QUERY_KEY, organizationId],
    queryFn: ({ signal }) => fetchDeviceTypes(authToken, signal),
    staleTime: Infinity,
    enabled: !!authToken,
  });
}

export type UseDevicesResult = DevicesState['devices'] & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export type UseDeviceTypesResult = DevicesState['deviceTypes'] & {
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export function useDevices(): UseDevicesResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  const query = useQuery(devicesQueryOptions(authToken, organizationId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDevicesGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const values = useMemo(() => query.data ?? [], [query.data]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: query.refetch,
    }),
    [values, lastUpdated, query.isLoading, query.isFetching, query.refetch]
  );
}

export function useDeviceTypes(): UseDeviceTypesResult {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();

  const query = useQuery(deviceTypesQueryOptions(authToken, organizationId));

  useEffect(() => {
    if (!(query.error instanceof Error)) return;
    if (query.error.message === TOKEN_INVALID) {
      logout();
    } else {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDeviceTypesGet',
          open: true,
          message: query.error.message,
          severity: 'error',
        });
    }
  }, [query.error]);

  const values = useMemo(() => query.data ?? [], [query.data]);

  const lastUpdated = useMemo(
    () =>
      query.dataUpdatedAt
        ? new Date(query.dataUpdatedAt).toISOString()
        : undefined,
    [query.dataUpdatedAt]
  );

  return useMemo(
    () => ({
      values,
      lastUpdated,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      refetch: query.refetch,
    }),
    [values, lastUpdated, query.isLoading, query.isFetching, query.refetch]
  );
}

function resolveDeviceLocation(
  organizations: OrganizationsState['organizations'],
  device: Device
): { country?: string; timezone?: string } {
  const organization = organizations.values.find(
    (o) => o.organizationId === device.organizationId
  );
  const location = organization?.locations.find(
    (l) => l.locationId === device.locationId
  );
  return { country: location?.country, timezone: location?.timezone };
}

function useDeviceWrite(opts: {
  caller: string;
  label: string;
  successMessage: string;
  sendRequest: (
    device: Device,
    authToken: string
  ) => Promise<ApiRespBody<unknown> | null>;
}) {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (device: Device) => opts.sendRequest(device, authToken),
    onMutate: () => {
      setAppLoading({ isLoading: true, label: opts.label });
    },
    onSuccess: (result) => {
      if (result?.success) {
        showSnackbar({
            autoHideDuration: 5000,
            caller: opts.caller,
            open: true,
            message: opts.successMessage,
            severity: 'success',
          });
        queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
      } else if (result?.message === TOKEN_INVALID) {
        logout();
      } else {
        showSnackbar({
            autoHideDuration: null,
            caller: opts.caller,
            open: true,
            message: result?.message ?? 'Request failed',
            severity: 'error',
          });
      }
    },
    onError: (err: unknown) => {
      showSnackbar({
          autoHideDuration: null,
          caller: opts.caller,
          open: true,
          message: err instanceof Error ? err.message : 'Request failed',
          severity: 'error',
        });
    },
    onSettled: () => {
      setAppLoading({ isLoading: false, label: '' });
    },
  });
}

export function useAddDevice() {
  const organizations = useOrganizationsList();
  const selectedEntityId = useSelectedEntityId();

  return useDeviceWrite({
    caller: 'onDeviceAdd',
    label: 'Adding Device',
    successMessage: 'Device Added',
    sendRequest: (device, authToken) => {
      const { country, timezone } = resolveDeviceLocation(organizations, device);
      return request<ApiRespBody<unknown>>('/api/devices', {
        method: 'POST',
        body: JSON.stringify({
          ...device,
          country,
          entityId: selectedEntityId,
          timezone,
        }),
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      });
    },
  });
}

export function useUpdateDevice() {
  const organizations = useOrganizationsList();

  return useDeviceWrite({
    caller: 'onDeviceUpdate',
    label: 'Updating Device',
    successMessage: 'Device Updated',
    sendRequest: (device, authToken) => {
      const { country, timezone } = resolveDeviceLocation(organizations, device);
      return request<ApiRespBody<unknown>>(`/api/devices/${device.deviceId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...device, country, timezone }),
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      });
    },
  });
}

export function useDeleteDevice() {
  return useDeviceWrite({
    caller: 'onDeviceDelete',
    label: 'Deleting Device',
    successMessage: 'Device Deleted',
    sendRequest: (device, authToken) =>
      request<ApiRespBody<unknown>>(`/api/devices/${device.deviceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          authorization: authToken,
        },
      }),
  });
}
